import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, ErroApi } from '../../nucleo/api';
import { textoDoErro, textoDoItem } from '../../nucleo/erros';
import { pode, useSessao } from '../../nucleo/sessao';
import { Chip } from '../../nucleo/ui/Tabela';
import type { EstadoCompetencia, Opcoes } from './tipos';
import { COR_ESTADO, formatarDinheiro } from './tipos';

/** M12.4: empresas do grupo que quem lê alcança (rota de opções do núcleo, já filtrada). */
export function useEmpresas() {
  return useQuery({ queryKey: ['nucleo', 'empresas', 'opcoes'], queryFn: () => api<{ id: string; nome: string }[]>('/api/v1/nucleo/empresas/opcoes'), staleTime: 60_000 });
}

/** M12.4: quem tem empresas permitidas não age sobre o que é do grupo (competência). */
export function useRestrito() {
  const eu = useSessao();
  return (eu.empresas_permitidas?.length ?? 0) > 0;
}

/**
 * SB-01: erros da API por campo no idioma da tela, com os códigos próprios da folha traduzidos pelo dicionário do
 * módulo (o errosPorCampo do núcleo só consulta o dicionário do núcleo). Mesma regra de mapeamento: campo_* com
 * details.campo vira extras.<campo>; código mapeado vai para o campo; o resto para o topo do formulário.
 */
export function errosDaFolha(e: unknown, mapa: Record<string, string>): { campos: Record<string, string>; geral: string | null } {
  if (!e) return { campos: {}, geral: null };
  if (!(e instanceof ErroApi)) return { campos: {}, geral: textoDoErro(e, 'folha') };
  const todos = e.erros.length ? e.erros : [{ code: e.code, message: e.message, details: e.details }];
  const campos: Record<string, string> = {};
  const restantes: string[] = [];
  for (const x of todos) {
    const campoExtra = (x.details as { campo?: string } | undefined)?.campo;
    const campo = campoExtra && x.code.startsWith('campo_') ? `extras.${campoExtra}` : mapa[x.code];
    const texto = textoDoItem(x, 'folha');
    if (campo && !campos[campo]) campos[campo] = texto;
    else restantes.push(texto);
  }
  return { campos, geral: restantes.length ? [...new Set(restantes)].join(' ') : null };
}

/** SB-01: texto de erro da API para toast, no idioma da tela. */
export const erroDaFolha = (e: unknown) => textoDoErro(e, 'folha');

export function useOpcoes() {
  return useQuery({ queryKey: ['folha', 'opcoes'], queryFn: () => api<Opcoes>('/api/v1/folha/config') });
}

/** Valor monetário. `quebravel`: num espaço estreito o valor quebra em vez de vazar ou ser cortado (VB-02). */
export function Dinheiro({ centavos, moeda = 'BRL', className = '', quebravel = false }: { centavos: string; moeda?: string; className?: string; quebravel?: boolean }) {
  const { i18n } = useTranslation();
  return <span className={`num ${quebravel ? 'wrap-anywhere' : 'whitespace-nowrap'} ${className}`}>{formatarDinheiro(centavos, moeda, i18n.language)}</span>;
}

export function ChipEstado({ estado }: { estado: EstadoCompetencia }) {
  const { t } = useTranslation('folha');
  return <Chip texto={t(`estado.${estado}`)} cor={COR_ESTADO[estado]} />;
}

/** Bloco com kicker e ação opcional. */
export function Bloco({ titulo, acao, children }: { titulo: string; acao?: ReactNode; children: ReactNode }) {
  return (
    <section className="min-w-0 rounded-xl border border-borda bg-sup-1 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="kicker">{titulo}</h2>
        {acao}
      </div>
      {children}
    </section>
  );
}

/**
 * Número com rótulo (totais da competência). VB-02: o valor nunca é truncado (valor monetário sem centavos engana);
 * se não couber, quebra dentro do cartão.
 */
export function Numero({ rotulo, children, destaque = false }: { rotulo: string; children: ReactNode; destaque?: boolean }) {
  return (
    <div className={`min-w-0 rounded-xl border border-borda p-4 ${destaque ? 'bg-acento-suave' : 'bg-sup-1'}`}>
      <p className="kicker mb-1 truncate">{rotulo}</p>
      <div className="text-lg font-medium wrap-anywhere">{children}</div>
    </div>
  );
}

/**
 * Botão primário do cabeçalho que abre o formulário pela URL (?novo=<qual>), conforme a permissão. `doGrupo`: só
 * para quem acessa todas as empresas (M12.4, a competência é do grupo).
 */
export function acaoNovo(permissao: string, rotulo: string, qual = '1', doGrupo = false) {
  return function AcaoNovo() {
    const { t } = useTranslation('folha');
    const eu = useSessao();
    const restrito = useRestrito();
    const [params, setParams] = useSearchParams();
    if (!pode(eu, permissao) || (doGrupo && restrito)) return null;
    return (
      <button type="button" onClick={() => setParams({ ...Object.fromEntries(params), novo: qual })} className="h-10 rounded-lg bg-acento px-4 text-sm font-medium text-acento-tinta hover:bg-acento-forte">
        {t(rotulo)}
      </button>
    );
  };
}

export function useNovo(qual = '1') {
  const [params, setParams] = useSearchParams();
  return {
    aberto: params.get('novo') === qual,
    abrir: () => setParams({ ...Object.fromEntries(params), novo: qual }),
    fechar: () => {
      params.delete('novo');
      setParams(params, { replace: true });
    },
  };
}
